'use client';

import Link from 'next/link';

const LAST_UPDATED = 'March 28, 2026';
const COMPANY_NAME = 'Commercial Law Publishers (India) Pvt. Ltd.';
const APP_NAME = 'Crack AIBE';
const CONTACT_EMAIL = 'info@crackaibe.com';
const WEBSITE = 'https://crackaibe.com';
const CONTACT_PHONE = '+919899633171';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 text-gray-800 hover:text-blue-600 transition-colors">
            <img
              src="/assets/images/Logo-1774427039733.png"
              alt="Crack AIBE Logo"
              className="h-8 w-auto object-contain"
            />
            <span className="font-semibold text-sm hidden sm:block">{APP_NAME}</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link href="/terms-and-conditions" className="text-blue-600 hover:text-blue-700 transition-colors">Terms &amp; Conditions</Link>
            <Link href="/refund-policy" className="text-blue-600 hover:text-blue-700 transition-colors">Refund Policy</Link>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
          <p className="text-sm text-gray-500">
            <span className="font-medium">Last Updated:</span> {LAST_UPDATED}
          </p>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            This Privacy Policy explains how {COMPANY_NAME} (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) collects, uses, stores, and protects your personal information when you use {APP_NAME} at <a href={WEBSITE} className="underline">{WEBSITE}</a>.
          </div>
        </div>

        <div className="space-y-10 text-gray-700 text-sm leading-relaxed">

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">1. Information We Collect</h2>
            <p>We collect the following categories of information:</p>
            <h3 className="font-semibold text-gray-900 mt-4 mb-2">1.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><strong>Account information:</strong> Name, email address, and password when you register.</li>
              <li><strong>Referral information:</strong> Referral codes you enter during registration.</li>
              <li><strong>Payment information:</strong> Payment is processed by Razorpay. We do not store card numbers or banking details on our servers.</li>
              <li><strong>Communications:</strong> Messages you send to our support team.</li>
            </ul>
            <h3 className="font-semibold text-gray-900 mt-4 mb-2">1.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><strong>Usage data:</strong> Pages visited, features used, test scores, time spent, and interaction patterns.</li>
              <li><strong>Device and session data:</strong> IP address, browser type, operating system, device identifiers, and session tokens. This data is used to enforce our single-device access policy.</li>
              <li><strong>Log data:</strong> Server logs including access times, error logs, and performance metrics.</li>
              <li><strong>Cookies and similar technologies:</strong> We use cookies to maintain your session and improve your experience.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Create and manage your account.</li>
              <li>Provide access to the platform and its features.</li>
              <li>Process payments and manage subscriptions.</li>
              <li>Personalise your learning experience and track your progress.</li>
              <li>Enforce our Terms &amp; Conditions, including the single-device access policy.</li>
              <li>Send transactional emails (account verification, payment receipts, subscription updates).</li>
              <li>Respond to your support queries.</li>
              <li>Improve the platform through analytics and user feedback.</li>
              <li>Comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">3. Data Sharing and Disclosure</h2>
            <p>We do not sell your personal data. We may share your information with:</p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li><strong>Service providers:</strong> Supabase (database and authentication), Razorpay (payment processing), and other infrastructure providers who process data on our behalf under strict confidentiality agreements.</li>
              <li><strong>Legal authorities:</strong> When required by law, court order, or governmental authority.</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, your data may be transferred to the acquiring entity.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">4. Data Retention</h2>
            <p>
              We retain your personal data for as long as your account is active or as needed to provide services. If you delete your account, we will delete or anonymise your personal data within 30 days, except where we are required to retain it for legal or regulatory purposes.
            </p>
            <p className="mt-3">
              Test results and performance data may be retained in anonymised form for platform improvement purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">5. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your personal information, including:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Encrypted data transmission using HTTPS/TLS.</li>
              <li>Secure authentication via Supabase with hashed passwords.</li>
              <li>Row-level security policies on our database.</li>
              <li>Regular security reviews and access controls.</li>
            </ul>
            <p className="mt-3">
              However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">6. Your Rights</h2>
            <p>You have the following rights regarding your personal data:</p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements).</li>
              <li><strong>Portability:</strong> Request your data in a machine-readable format.</li>
              <li><strong>Objection:</strong> Object to certain types of processing.</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>. We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">7. Cookies</h2>
            <p>
              We use essential cookies to maintain your login session and ensure the platform functions correctly. We do not use third-party advertising cookies. You can control cookie settings through your browser, but disabling essential cookies may affect platform functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">8. Children&apos;s Privacy</h2>
            <p>
              {APP_NAME} is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that we have collected data from a minor, we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by email or through an in-app notice. Your continued use of the platform after any changes constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">10. Contact Us</h2>
            <div className="p-5 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
              <p><span className="font-semibold text-gray-900">Company:</span> {COMPANY_NAME}</p>
              <p>
                <span className="font-semibold text-gray-900">Email:</span>{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>
              </p>
              <p>
                <span className="font-semibold text-gray-900">Phone / WhatsApp:</span>{' '}
                <a href={`tel:${CONTACT_PHONE}`} className="text-blue-600 hover:underline">{CONTACT_PHONE}</a>
              </p>
              <p>
                <span className="font-semibold text-gray-900">Website:</span>{' '}
                <a href={WEBSITE} className="text-blue-600 hover:underline">{WEBSITE}</a>
              </p>
            </div>
          </section>

        </div>

        <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
          <p>© {new Date()?.getFullYear()} {COMPANY_NAME}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/terms-and-conditions" className="hover:text-gray-600 transition-colors">Terms &amp; Conditions</Link>
            <Link href="/refund-policy" className="hover:text-gray-600 transition-colors">Refund Policy</Link>
            <Link href="/login" className="hover:text-gray-600 transition-colors">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
